import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { Gender } from "@/lib/models/nolaContest";
import {
  CONTEST_ENTRY_HISTORY_OF_AWARDS_MAX_LENGTH,
  CONTEST_ENTRY_PENNAME_MAX_LENGTH,
} from "@/lib/utils/contestEntryApplicantForm";
import IconCheck from "~icons/figma/Check";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconClose from "~icons/figma/Close";
import IconPlus from "~icons/figma/Plus";
import {
  createContestEntryApplicantInfoFormEventHandlers,
  type ContestEntryApplicantFormValue,
} from "./contestEntryApplicantInfoFormEventHandlers";

export type { ContestEntryApplicantFormValue };


export default /*#__PURE__*/_defineComponent({
  __name: 'ContestEntryApplicantInfoForm',
  props: {
    value: { type: null, required: true },
    pennameOptions: { type: Array, required: true }
  },
  emits: ["input", "add-penname"],
  setup(__props: any, { emit }: { emit: ({
  (e: "input", value: ContestEntryApplicantFormValue): void;
  (e: "add-penname", name: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: ContestEntryApplicantFormValue;
  pennameOptions: string[];
};

const pennameMaxLength = CONTEST_ENTRY_PENNAME_MAX_LENGTH;
const historyOfAwardsMaxLength = CONTEST_ENTRY_HISTORY_OF_AWARDS_MAX_LENGTH;





const { t } = useI18n();

function updateField<K extends keyof ContestEntryApplicantFormValue>(
  field: K,
  fieldValue: ContestEntryApplicantFormValue[K],
) {
  emit("input", { ...props.value, [field]: fieldValue });
}

const handlers = createContestEntryApplicantInfoFormEventHandlers(updateField);

const genderOptions = computed(() =>
  Object.entries(Gender)
    .filter(([_, genderValue]) => !Number.isNaN(Number(genderValue)))
    .map(([key, genderValue]) => ({
      value: String(genderValue),
      label: t(`common.gender.${key}`),
    })),
);

const pennameFieldRef = ref<HTMLElement | null>(null);
const newPennameInputRef = ref<HTMLInputElement | null>(null);
const isPennameMenuOpen = ref(false);
const isAddPennameOpen = ref(false);
const newPenname = ref("");

const canAddPenname = computed(() => newPenname.value.trim().length > 0);

function togglePennameMenu() {
  isPennameMenuOpen.value = !isPennameMenuOpen.value;
}

function onSelectPenname(name: string) {
  updateField("penname", name);
  isPennameMenuOpen.value = false;
}

function openAddPennameDialog() {
  isPennameMenuOpen.value = false;
  newPenname.value = "";
  isAddPennameOpen.value = true;
}

function closeAddPennameDialog() {
  isAddPennameOpen.value = false;
  newPenname.value = "";
}

function onConfirmAddPenname() {
  const name = newPenname.value.trim();
  if (!name) return;
  if (props.pennameOptions.includes(name)) {
    onSelectPenname(name);
  } else {
    emit("add-penname", name);
    isPennameMenuOpen.value = false;
  }
  closeAddPennameDialog();
}

function onDocumentPointerDown(event: Event) {
  if (!isPennameMenuOpen.value) return;
  const { target } = event;
  if (!(target instanceof Node)) return;
  if (pennameFieldRef.value?.contains(target)) return;
  isPennameMenuOpen.value = false;
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  if (isAddPennameOpen.value) {
    closeAddPennameDialog();
    return;
  }
  isPennameMenuOpen.value = false;
}

watch(isAddPennameOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
  if (!open) return;
  nextTick(() => {
    newPennameInputRef.value?.focus();
  });
});

onMounted(() => {
  document.addEventListener("pointerdown", onDocumentPointerDown);
  document.addEventListener("keydown", onDocumentKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onDocumentPointerDown);
  document.removeEventListener("keydown", onDocumentKeydown);
  document.body.style.overflow = "";
});

const genderSelectValue = computed({
  get: () => {
    const { gender } = props.value;
    if (gender === Gender.male || gender === Gender.female || gender === Gender.other) {
      return String(gender);
    }
    return "";
  },
  set: (selected: string) => {
    updateField("gender", selected ? Number(selected) : 0);
  },
});

return { __sfc: true,pennameMaxLength, historyOfAwardsMaxLength, props, emit, t, updateField, handlers, genderOptions, pennameFieldRef, newPennameInputRef, isPennameMenuOpen, isAddPennameOpen, newPenname, canAddPenname, togglePennameMenu, onSelectPenname, openAddPennameDialog, closeAddPennameDialog, onConfirmAddPenname, onDocumentPointerDown, onDocumentKeydown, genderSelectValue, IconCheck, IconChevronDown, IconClose, IconPlus }
}

})